import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Calendar as CalendarIcon, MapPin, Users, Edit, Trash2, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

export const GamesPage = () => {
  const { api, user } = useAuth();
  const [games, setGames] = useState([]);
  const [clients, setClients] = useState([]);
  const [referees, setReferees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedReferees, setSelectedReferees] = useState([]);
  const [formData, setFormData] = useState({
    tournament_event: '',
    location: '',
    game_date: new Date(),
    division: '',
    num_games: 1,
    officials_needed: 2,
    rate: 50.0,
    client_id: '',
    notes: ''
  });

  const fetchData = async () => {
    try {
      const [gamesRes, clientsRes, refereesRes] = await Promise.all([
        api().get('/games'),
        api().get('/clients'),
        api().get('/referees')
      ]);
      setGames(gamesRes.data);
      setClients(clientsRes.data);
      setReferees(refereesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedGame) {
        await api().put(`/games/${selectedGame.id}`, formData);
        toast.success('Game updated successfully');
      } else {
        await api().post('/games', formData);
        toast.success('Game created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save game');
    }
  };

  const handleDelete = async (gameId) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;
    try {
      await api().delete(`/games/${gameId}`);
      toast.success('Game deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete game');
    }
  };

  const handleStatusChange = async (gameId, status) => {
    try {
      await api().put(`/games/${gameId}/status?status=${status}`);
      toast.success(`Game marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAssignReferees = async () => {
    if (!selectedGame) return;
    try {
      await api().put(`/games/${selectedGame.id}/assign`, selectedReferees);
      toast.success('Referees assigned successfully');
      setAssignDialogOpen(false);
      setSelectedGame(null);
      setSelectedReferees([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign referees');
    }
  };

  const openEditDialog = (game) => {
    setSelectedGame(game);
    setFormData({
      tournament_event: game.tournament_event,
      location: game.location,
      game_date: new Date(game.game_date),
      division: game.division,
      num_games: game.num_games,
      officials_needed: game.officials_needed,
      rate: game.rate,
      client_id: game.client_id,
      notes: game.notes || ''
    });
    setDialogOpen(true);
  };

  const openAssignDialog = (game) => {
    setSelectedGame(game);
    setSelectedReferees(game.assigned_referees || []);
    setAssignDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedGame(null);
    setFormData({
      tournament_event: '',
      location: '',
      game_date: new Date(),
      division: '',
      num_games: 1,
      officials_needed: 2,
      rate: 50.0,
      client_id: '',
      notes: ''
    });
  };

  const toggleRefereeSelection = (refId) => {
    setSelectedReferees(prev => 
      prev.includes(refId) ? prev.filter(id => id !== refId) : [...prev, refId]
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-900/30 text-blue-400 border-blue-900',
      in_progress: 'bg-yellow-900/30 text-yellow-400 border-yellow-900',
      completed: 'bg-green-900/30 text-green-400 border-green-900',
      cancelled: 'bg-red-900/30 text-red-400 border-red-900'
    };
    return colors[status] || colors.scheduled;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  const canEdit = user?.role === 'admin' || user?.role === 'assignor';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="games-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-wide">Games</h1>
          <p className="text-muted-foreground mt-1">Manage game schedules and assignments</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-game-btn" className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Game
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase">{selectedGame ? 'Edit Game' : 'New Game'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Tournament/Event</Label>
                  <Input
                    value={formData.tournament_event}
                    onChange={(e) => setFormData({ ...formData, tournament_event: e.target.value })}
                    placeholder="State Basketball Tournament"
                    required
                    data-testid="game-event-input"
                    className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Location</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Denver, CO"
                      required
                      data-testid="game-location-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Division</Label>
                    <Input
                      value={formData.division}
                      onChange={(e) => setFormData({ ...formData, division: e.target.value })}
                      placeholder="Varsity"
                      required
                      data-testid="game-division-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Game Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" data-testid="game-date-picker" className="w-full justify-start text-left bg-zinc-800 border-zinc-700 h-10 rounded-sm">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(formData.game_date, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                      <Calendar
                        mode="single"
                        selected={formData.game_date}
                        onSelect={(date) => date && setFormData({ ...formData, game_date: date })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500"># of Games</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.num_games}
                      onChange={(e) => setFormData({ ...formData, num_games: parseInt(e.target.value) })}
                      required
                      data-testid="game-num-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Officials Needed</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.officials_needed}
                      onChange={(e) => setFormData({ ...formData, officials_needed: parseInt(e.target.value) })}
                      required
                      data-testid="game-officials-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Rate ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.rate}
                      onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) })}
                      required
                      data-testid="game-rate-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Client</Label>
                  <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                    <SelectTrigger data-testid="game-client-select" className="bg-zinc-800 border-zinc-700 h-10 rounded-sm">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>{client.organization_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    data-testid="game-notes-input"
                    className="bg-zinc-800 border-zinc-700 rounded-sm min-h-[80px]"
                  />
                </div>

                <Button type="submit" data-testid="game-submit-btn" className="w-full bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm h-10">
                  {selectedGame ? 'Update Game' : 'Create Game'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Games List */}
      {games.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-zinc-900/50 border border-zinc-800">
          <CalendarIcon className="w-12 h-12 text-zinc-600 mb-4" />
          <p className="text-zinc-500 text-lg">No games scheduled yet</p>
          {canEdit && <p className="text-zinc-600 text-sm mt-2">Click "Add Game" to create your first game</p>}
        </div>
      ) : (
        <div className="space-y-4">
          {games.map((game) => (
            <div 
              key={game.id}
              data-testid={`game-card-${game.id}`}
              className="bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors"
            >
              <div className="p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-blue-900/20 rounded-sm">
                      <CalendarIcon className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg">{game.tournament_event}</h3>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-zinc-400">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {game.location}
                        </span>
                        <span>{formatDate(game.game_date)}</span>
                        <span>{game.division}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-sm">
                    <span className="text-zinc-500">{game.num_games} games</span>
                    <span className="mx-2 text-zinc-700">•</span>
                    <span className="text-zinc-500">{game.officials_needed} officials</span>
                    <span className="mx-2 text-zinc-700">•</span>
                    <span className="text-emerald-400 font-medium">${game.rate}/game</span>
                  </div>
                  <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${getStatusColor(game.status)}`}>
                    {game.status.replace('_', ' ')}
                  </span>
                </div>
              </div>

              {/* Assigned Referees */}
              <div className="px-5 pb-4 border-t border-zinc-800 pt-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-zinc-500" />
                    <span className="text-sm text-zinc-500">
                      {game.assigned_referees.length} / {game.officials_needed} Officials Assigned
                    </span>
                    {game.assigned_referees.length > 0 && (
                      <span className="text-sm text-zinc-400">
                        ({referees.filter(r => game.assigned_referees.includes(r.id)).map(r => r.name).join(', ')})
                      </span>
                    )}
                  </div>

                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openAssignDialog(game)}
                        data-testid={`assign-referees-btn-${game.id}`}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Users className="w-4 h-4 mr-1" /> Assign
                      </Button>
                      {game.status !== 'completed' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleStatusChange(game.id, 'completed')}
                          data-testid={`complete-game-btn-${game.id}`}
                          className="text-emerald-400 hover:text-emerald-300"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Complete
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(game)}
                        data-testid={`edit-game-btn-${game.id}`}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(game.id)}
                        data-testid={`delete-game-btn-${game.id}`}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Assign Referees Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Assign Referees</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            {referees.length === 0 ? (
              <p className="text-zinc-500 text-center py-4">No referees available. Add referees first.</p>
            ) : (
              referees.map((referee) => (
                <div
                  key={referee.id}
                  onClick={() => toggleRefereeSelection(referee.id)}
                  data-testid={`referee-option-${referee.id}`}
                  className={`p-3 border cursor-pointer transition-colors ${
                    selectedReferees.includes(referee.id)
                      ? 'border-emerald-500 bg-emerald-900/20'
                      : 'border-zinc-800 hover:border-zinc-700'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{referee.name}</p>
                      <p className="text-sm text-zinc-500">{referee.email}</p>
                    </div>
                    {selectedReferees.includes(referee.id) && (
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          <Button
            onClick={handleAssignReferees}
            data-testid="confirm-assign-btn"
            className="w-full mt-4 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm"
          >
            Assign {selectedReferees.length} Referee{selectedReferees.length !== 1 ? 's' : ''}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
